import { useEffect, useMemo } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import { api } from '../api/client';
import type { Meet, ScheduleEntry, Student } from '../api/types';

export default function CoachPackPage() {
  const { id } = useParams();
  const meetId = Number(id);
  const meetQuery = useQuery<Meet>({ queryKey: ['meets', meetId], queryFn: async () => (await api.get(`/meets/${meetId}`)).data });
  const scheduleQuery = useQuery<{ entries: ScheduleEntry[] }>({ queryKey: ['schedule', 'meet', meetId], queryFn: async () => (await api.get(`/schedule/meet/${meetId}`)).data });
  const studentsQuery = useQuery<Student[]>({ queryKey: ['students'], queryFn: async () => (await api.get('/students')).data });

  useEffect(() => {
    document.title = meetQuery.data ? `${meetQuery.data.name} Coach Pack — Baton` : 'Coach Pack — Baton';
  }, [meetQuery.data]);

  const entries = scheduleQuery.data?.entries ?? [];
  const eventGroups = useMemo(() => groupBy(entries, (e) => `${e.event_name} · ${e.division}${e.age_group ? ` · ${e.age_group}` : ''}`), [entries]);
  const athleteGroups = useMemo(() => groupBy(entries, (e) => e.student_name), [entries]);
  const enteredStudentIds = new Set(entries.map((e) => e.student_id));
  const coverageRows = (studentsQuery.data ?? []).filter((s) => enteredStudentIds.has(s.id));

  if (meetQuery.isLoading || scheduleQuery.isLoading || studentsQuery.isLoading) return <LoadingFallback />;
  const meet = meetQuery.data;
  if (!meet) return <EmptyState title="Meet not found" />;

  return (
    <Box>
      <PageHeader
        title={`${meet.name} Coach Pack`}
        subtitle="Browser-printable event rosters, athlete schedules, parent coverage, and check-in sheets. Use Print to save as PDF."
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()} aria-label="Print coach pack">
              Print / Save PDF
            </Button>
            <Button component={RouterLink} to={`/meets/${meetId}/day`} variant="contained" startIcon={<PlayCircleIcon />}>
              Meet Day
            </Button>
          </Stack>
        }
      />

      <Container maxWidth="lg" disableGutters className="coach-pack">
        <PackSection title="Meet summary">
          <Grid container spacing={2}>
            <Summary label="Date" value={`${dayjs(meet.start_date).format('MMM D, YYYY')}${meet.end_date !== meet.start_date ? ` - ${dayjs(meet.end_date).format('MMM D')}` : ''}`} />
            <Summary label="Venue" value={meet.venue?.name || 'TBD'} />
            <Summary label="Entries" value={entries.length} />
            <Summary label="Athletes" value={new Set(entries.map((e) => e.student_id)).size} />
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Check-in instructions</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{meet.check_in_instructions || 'None provided.'}</Typography>
            </Grid>
          </Grid>
        </PackSection>

        <PackSection title="Event roster">
          {entries.length === 0 ? <EmptyState title="No entries yet" /> : (
            <Stack spacing={2}>
              {Object.entries(eventGroups).map(([eventName, rows]) => (
                <Box key={eventName} sx={{ breakInside: 'avoid' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{eventName}</Typography>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Athlete</TableCell><TableCell>When</TableCell><TableCell>Heat/Lane</TableCell><TableCell>Seed</TableCell></TableRow></TableHead>
                    <TableBody>
                      {rows.map((e) => (
                        <TableRow key={e.registration_id}>
                          <TableCell>{e.student_name}</TableCell>
                          <TableCell>{e.scheduled_start_at ? dayjs(e.scheduled_start_at).format('h:mm A') : 'TBD'}</TableCell>
                          <TableCell>{e.heat_number ? `H${e.heat_number}${e.lane ? ` · L${e.lane}` : ''}` : e.lane ? `L${e.lane}` : '—'}</TableCell>
                          <TableCell>{e.seed_mark || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Stack>
          )}
        </PackSection>

        <PackSection title="Athlete schedules">
          <Grid container spacing={2}>
            {Object.entries(athleteGroups).map(([studentName, rows]) => (
              <Grid item xs={12} md={6} key={studentName}>
                <Card variant="outlined" sx={{ height: '100%', breakInside: 'avoid' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{studentName}</Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {rows.map((e) => (
                        <Typography key={e.registration_id} variant="body2">
                          {e.scheduled_start_at ? dayjs(e.scheduled_start_at).format('h:mm A') : 'TBD'} · {e.event_name} · {e.area || e.venue_name || 'TBD'}
                        </Typography>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </PackSection>

        <PackSection title="Parent coverage">
          <Table size="small">
            <TableHead><TableRow><TableCell>Athlete</TableCell><TableCell>Parent emails</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
            <TableBody>
              {coverageRows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.full_name}</TableCell>
                  <TableCell>{s.parents.map((p) => p.email).join(', ') || '—'}</TableCell>
                  <TableCell><Chip size="small" color={s.parents.length ? 'success' : 'warning'} label={s.parents.length ? 'Covered' : 'Missing'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PackSection>

        <PackSection title="Check-in sheet">
          <Table size="small">
            <TableHead><TableRow><TableCell>Athlete</TableCell><TableCell>Here</TableCell><TableCell>Missing</TableCell><TableCell>Scratched</TableCell><TableCell>Notes</TableCell></TableRow></TableHead>
            <TableBody>
              {Array.from(new Map(entries.map((e) => [e.student_id, e.student_name])).entries()).map(([id, name]) => (
                <TableRow key={id}>
                  <TableCell>{name}</TableCell>
                  <TableCell>□</TableCell>
                  <TableCell>□</TableCell>
                  <TableCell>□</TableCell>
                  <TableCell sx={{ minWidth: 180 }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PackSection>
      </Container>
      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, button { display: none !important; }
          body { background: #fff !important; }
          .coach-pack { max-width: none !important; }
          .MuiCard-root { box-shadow: none !important; }
        }
      `}</style>
    </Box>
  );
}

function PackSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card sx={{ mb: 2, breakInside: 'avoid' }} variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Divider sx={{ my: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function Summary({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid item xs={6} md={3}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{value}</Typography>
    </Grid>
  );
}

function groupBy<T>(rows: T[], keyFn: (row: T) => string): Record<string, T[]> {
  return rows.reduce<Record<string, T[]>>((acc, row) => {
    const key = keyFn(row);
    acc[key] ??= [];
    acc[key].push(row);
    return acc;
  }, {});
}
